import { createEventEmitter, type Event, type EventEmitter } from "@core/Event/EventEmitter";
import type { ClientArticleProps } from "@core/SitePresenter/SitePresenter";
import FetchService from "@core-ui/ApiServices/FetchService";
import type { CommentBlock } from "@core-ui/CommentBlock";
import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import ArticleRefService from "@core-ui/ContextServices/ArticleRef";
import CommentView from "@ext/markdown/elements/comment/edit/components/View/CommentView";
import CommentBlockMark from "@ext/markdown/elements/comment/edit/logic/BlockMark";
import useCommentsStorage from "@ext/markdown/elements/comment/edit/logic/hooks/useCommentsStorage";
import { getEditorContext } from "@ext/markdown/elementsUtils/editorContext/EditorContext";
import { useReviewStore } from "@ext/review/logic/store/ReviewStore";
import { scrollToReviewItem } from "@ext/review/logic/utils/scrollToReviewItem";
import type { Editor, Range } from "@tiptap/core";
import { useEditorState } from "@tiptap/react";
import { createContext, memo, type RefObject, useCallback, useEffect, useRef, useState } from "react";

interface CommentEditorProviderProps {
	editor: Editor;
	children: JSX.Element;
	articlePropsRef?: RefObject<ClientArticleProps>;
	onCommentSaved?: (id: string, comment: CommentBlock) => void;
	scopedArticle?: boolean;
}

export type CommentEditorEvents = Event<"delete", { id: string }> & Event<"update", { id: string }>;

export const CommentEditorEventsContext = createContext<EventEmitter<CommentEditorEvents>>(null);

const CommentEditorProvider = (props: CommentEditorProviderProps): JSX.Element => {
	const { editor, children, articlePropsRef, onCommentSaved, scopedArticle = false } = props;
	const [events, setEvents] = useState<EventEmitter<CommentEditorEvents>>(null);
	const articleRef = ArticleRefService.value;
	const { pendingItem, setPendingItem } = useReviewStore((s) => ({
		pendingItem: s.pendingItem,
		setPendingItem: s.setPendingItem,
	}));

	const commentsArticlePath = scopedArticle ? undefined : articlePropsRef?.current?.ref?.path;
	useCommentsStorage(editor, commentsArticlePath);

	useEffect(() => {
		const eventEmmiter = createEventEmitter<CommentEditorEvents>();
		setEvents(eventEmmiter);
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	useEffect(() => {
		editor.once("create", () => {
			if (!pendingItem) return;

			if (!articleRef.current) return;
			scrollToReviewItem(articleRef.current, pendingItem, editor);
			setPendingItem(null);
		});
	}, [editor, articleRef]);

	const apiUrlCreator = ApiUrlCreator.value;

	const openedCommentId = useEditorState({
		editor,
		selector: ({ editor }) => editor?.storage?.comment?.openedComment?.id ?? null,
	});

	// Bumped on creator/scope change so a response to an older request never reaches the cache.
	const contextGeneration = useRef(0);
	// biome-ignore lint/correctness/useExhaustiveDependencies: the generation must bump exactly on creator and scope change
	useEffect(() => {
		contextGeneration.current += 1;
	}, [apiUrlCreator, scopedArticle]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: articlePropsRef is stable; read current path when called
	const loadComment = useCallback(
		async (id: string) => {
			const cached = editor.storage.comment.comments.get(id);
			if (cached) return cached;

			const generation = contextGeneration.current;
			const url = apiUrlCreator.getComment(id, scopedArticle ? undefined : articlePropsRef?.current?.ref?.path);
			const res = await FetchService.fetch<CommentBlock>(url);
			if (!res.ok || contextGeneration.current !== generation) return;

			const comment = await res.json();
			if (contextGeneration.current !== generation) return;
			if (comment) editor.storage.comment.comments.set(id, comment);
			return comment;
		},
		[editor, apiUrlCreator, scopedArticle],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: articlePropsRef is stable; read current path when called
	const saveComment = useCallback(
		(id: string, comment: CommentBlock) => {
			editor.storage.comment.comments.set(id, comment);
			editor.storage.comment.deleted.delete(id);

			if (getEditorContext(editor).noWrite) return;

			const url = apiUrlCreator.updateComment(
				id,
				scopedArticle ? undefined : articlePropsRef?.current?.ref?.path,
			);
			void FetchService.fetch(url, JSON.stringify(comment)).then((res) => {
				if (!res.ok) return;
				events.emit("update", { id });
				onCommentSaved?.(id, comment);
			});
		},
		[editor, events, onCommentSaved, apiUrlCreator, scopedArticle],
	);

	const deleteComment = useCallback(
		(id: string, positions: Range[]) => {
			const blockMark = new CommentBlockMark(editor.state.tr, editor.schema.marks.comment);
			const tr = blockMark.deleteMarkup(positions);
			editor.view.dispatch(tr);
			events.emit("delete", { id });
		},
		[editor, events],
	);

	return (
		<CommentEditorEventsContext.Provider value={events}>
			<CommentView
				commentId={openedCommentId}
				deleteComment={deleteComment}
				editor={editor}
				loadComment={loadComment}
				saveComment={saveComment}
			/>
			{children}
		</CommentEditorEventsContext.Provider>
	);
};

export default memo(CommentEditorProvider);
